import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import Table from './Table';
import RequestLogBadge from './RequestLogBadge';
import { DateTime } from 'luxon';
import formatDate from '../helpers/formatDate';

// Kept modest on purpose: this app's Puma config (config/puma.rb) defaults to only
// 5 threads server-wide, shared by every user's requests, not just this session's
// bulk sync. Higher concurrency here would just queue behind the same thread pool
// (and could starve other concurrent requests) rather than actually go faster.
const BULK_SYNC_CONCURRENCY = 3;

// #bulk_sync_timestamps handles a batch that may span several distinct HubSpot
// timestamps; #bulk_sync_shared_timestamp (see the "Shared Timestamps" chips) is
// specialized for a batch that shares one exact timestamp, letting the backend
// write every eligible record in a single UPDATE instead of one per record.
const DEFAULT_TIMESTAMP_SYNC_ENDPOINT = 'api/engagements/bulk_sync_timestamps';
const SHARED_TIMESTAMP_SYNC_ENDPOINT = 'api/engagements/bulk_sync_shared_timestamp';
// Queues every shared-timestamp group for background processing instead of syncing
// them from the browser — see "Sync All Groups Async" below.
const SHARED_TIMESTAMP_ASYNC_SYNC_ENDPOINT = 'api/engagements/bulk_sync_all_groups_async';

// How many of the largest Shared Timestamp groups "Sync Top 10 Groups" fires one
// request each for — capped so one click can't kick off an unbounded number of
// requests on a day with many distinct shared timestamps. "Sync Top 20 Groups"
// uses the larger cap below for days with more distinct shared timestamps.
const MAX_MASS_SYNC_GROUPS = 10;
const MAX_MASS_SYNC_GROUPS_EXTENDED = 20;

// Approximate height of two wrapped rows of Shared Timestamps chips (see
// SHARED_TIMESTAMPS_ROW_HEIGHT usage) — chips vary slightly in width but not in
// height, so this collapses/expands consistently without measuring the DOM.
const SHARED_TIMESTAMPS_COLLAPSED_HEIGHT = '68px';

// Runs `worker` over `items` with at most `concurrency` in flight at once,
// preserving per-item side effects as each one starts/finishes.
async function runWithConcurrency(items, concurrency, worker) {
  let nextIndex = 0;

  async function runNext() {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      await worker(items[currentIndex], currentIndex);
    }
  }

  const workerCount = Math.min(concurrency, items.length);
  await Promise.all(Array.from({ length: workerCount }, runNext));
}

export default function EngagementsTable() {
  const [ffData, setFFData] = useState([]);
  const [leadLookup, setLeadLookup] = useState({});
  const [loadingData, setLoadingData] = useState(false);
  const [hubspotData, setHubspotData] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loadDurationSeconds, setLoadDurationSeconds] = useState(null);
  const [requestLog, setRequestLog] = useState([]);
  const [requestLogModalOpen, setRequestLogModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState(null);
  const [successToastMessage, setSuccessToastMessage] = useState(null);
  const [jsonModal, setJsonModal] = useState(null);
  const [syncingRecordIds, setSyncingRecordIds] = useState([]);
  const [bulkSyncConfirmation, setBulkSyncConfirmation] = useState({
    isOpen: false,
    records: [],
  });
  const [bulkSyncState, setBulkSyncState] = useState({
    isOpen: false,
    isRunning: false,
    total: 0,
    completed: 0,
    currentLabel: null,
    failures: [],
  });
  const [bulkTimestampSyncConfirmation, setBulkTimestampSyncConfirmation] = useState({
    isOpen: false,
    records: [],
    endpoint: DEFAULT_TIMESTAMP_SYNC_ENDPOINT,
    contextLabel: 'from the current page',
  });
  const [bulkTimestampSyncState, setBulkTimestampSyncState] = useState({
    isRunning: false,
  });
  const [sharedTimestampsExpanded, setSharedTimestampsExpanded] = useState(false);
  const [sharedTimestampMassSyncConfirmation, setSharedTimestampMassSyncConfirmation] = useState({
    isOpen: false,
    groups: [],
  });
  const [sharedTimestampMassSyncState, setSharedTimestampMassSyncState] = useState({
    isOpen: false,
    isRunning: false,
    totalGroups: 0,
    completedGroups: 0,
    currentLabel: null,
    results: [],
  });
  const [asyncGroupSyncConfirmation, setAsyncGroupSyncConfirmation] = useState({
    isOpen: false,
    groups: [],
  });
  const [asyncGroupSyncState, setAsyncGroupSyncState] = useState({
    isSubmitting: false,
  });
  // Recent EngagementSyncBatch summaries (newest first) — polled from the backend
  // so progress for a "Sync All Groups Async" run is visible on this page even after
  // the queueing request returns (and even across a reload, since batches persist).
  const [asyncBatches, setAsyncBatches] = useState([]);
  const [expandedAsyncBatchId, setExpandedAsyncBatchId] = useState(null);
  const [asyncBatchResultsById, setAsyncBatchResultsById] = useState({});
  // Bumped to force the main engagements fetch (below) to re-run without changing
  // the date filter — see checkForNewlyFinishedBatches, which bumps this once a
  // batch we watched go from running to finished, so the page's own sync-status
  // counts/rows (which the async path never patches directly) catch up on their own
  // instead of requiring a manual reload.
  const [reloadNonce, setReloadNonce] = useState(0);
  // Batch ids seen not-yet-finished during this page load — only a batch that was
  // actually watched transitioning (not one already finished before this page even
  // started polling) should trigger the reload above.
  const seenUnfinishedBatchIdsRef = useRef(new Set());
  const notifiedFinishedBatchIdsRef = useRef(new Set());
  const [syncPreview, setSyncPreview] = useState({
    isOpen: false,
    isLoading: false,
    isSaving: false,
    record: null,
    fields: null,
    isNewRecord: false,
    error: null,
  });
  const activeSyncKeysRef = useRef(new Set());
  const bulkSyncRunningRef = useRef(false);
  const tableInstanceRef = useRef(null);
  // Mirrors the table's sync_status column filter so content rendered outside
  // <Table> (e.g. the Shared Timestamps card below) can react to it — the
  // toolbar buttons don't need this since renderToolbar already gets a fresh
  // table instance on every filter change.
  const [activeSyncStatusFilter, setActiveSyncStatusFilter] = useState(undefined);

  // Toggles the Sync Status column filter — clicking an already-active status
  // clears it. Reads the current value at click time via the table instance
  // captured from renderToolbar, so it doesn't need filter state lifted into
  // this component just to stay in sync.
  const toggleSyncStatusFilter = useCallback((status) => {
    const column = tableInstanceRef.current?.getColumn('sync_status');
    if (!column) return;

    column.setFilterValue((prev) => (prev === status ? undefined : status));
  }, []);

  // Narrows to out_of_sync_timestamp_only records sharing one exact
  // hs_lastmodifieddate (see sharedTimestampGroups) by combining the sync_status
  // filter with the hs_version_timestamp column filter. Clicking an already-active
  // timestamp clears just the timestamp constraint, leaving the status filter in
  // place — consistent with toggleSyncStatusFilter's toggle behavior.
  const toggleSharedTimestampFilter = useCallback((timestamp) => {
    const table = tableInstanceRef.current;
    if (!table) return;

    const syncStatusColumn = table.getColumn('sync_status');
    const timestampColumn = table.getColumn('hs_version_timestamp');
    if (!syncStatusColumn || !timestampColumn) return;

    const isActive = syncStatusColumn.getFilterValue() === 'out_of_sync_timestamp_only'
      && timestampColumn.getFilterValue() === timestamp;

    if (isActive) {
      timestampColumn.setFilterValue(undefined);
    } else {
      syncStatusColumn.setFilterValue('out_of_sync_timestamp_only');
      timestampColumn.setFilterValue(timestamp);
    }
  }, []);

  const getRecordKey = useCallback((record) => {
    if (record.ff_record?.id) return `ff-${record.ff_record.id}`;
    if (record.hs_record?.engagement?.id) return `hs-${record.hs_record.engagement.id}`;
    if (record.ff_record?.hubspot_id) return `hubspot-${record.ff_record.hubspot_id}`;
    return 'unknown-record';
  }, []);

  const getRecordLabel = useCallback((record) => {
    return (
      record.hs_record?.resolved?.deal_names?.join('; ') ||
      record.hs_record?.resolved?.contact_names?.join('; ') ||
      record.ff_record?.id ||
      record.hs_record?.engagement?.id ||
      'Unknown engagement'
    );
  }, []);

  const canSyncRecord = useCallback((record) => {
    return (
      ['synced', 'out_of_sync', 'out_of_sync_timestamp_only', 'hs_only'].includes(record.sync_status) &&
      Boolean(record.hs_record?.engagement?.id) &&
      record.has_matching_lead
    );
  }, []);

  // mergeUpdatedRecord queues updates and flushes them in a single batched pass
  // instead of updating state per-record. With ~16k records loaded, doing a full
  // .map() over ffData/hubspotData (plus the downstream combinedData/statusCounts/
  // table-internals recompute it triggers) for every individual sync completion
  // made bulk sync (which streams in dozens of completions) visibly slow down as
  // more data was loaded. Batching collapses many rapid completions into one pass.
  const pendingMergeQueueRef = useRef([]);
  const mergeFlushScheduledRef = useRef(false);

  const flushMergeQueue = useCallback(() => {
    mergeFlushScheduledRef.current = false;
    const queued = pendingMergeQueueRef.current;
    if (queued.length === 0) return;
    pendingMergeQueueRef.current = [];

    const normalizedRecords = queued.map((updatedRecord) => (
      typeof updatedRecord === 'string' ? JSON.parse(updatedRecord) : updatedRecord
    ));

    setFFData((prev) => {
      const indexById = new Map();
      prev.forEach((engagement, index) => {
        if (engagement.id != null) indexById.set(`id-${engagement.id}`, index);
        if (engagement.hubspot_id != null) indexById.set(`hs-${engagement.hubspot_id}`, index);
      });

      const next = [...prev];

      normalizedRecords.forEach((normalizedRecord) => {
        const idKey = normalizedRecord.id != null ? `id-${normalizedRecord.id}` : null;
        const hsKey = normalizedRecord.hubspot_id != null ? `hs-${normalizedRecord.hubspot_id}` : null;
        const existingIndex = (idKey && indexById.get(idKey)) ?? (hsKey && indexById.get(hsKey));

        if (existingIndex != null) {
          next[existingIndex] = normalizedRecord;
        } else {
          next.push(normalizedRecord);
          if (idKey) indexById.set(idKey, next.length - 1);
          if (hsKey) indexById.set(hsKey, next.length - 1);
        }
      });

      return next;
    });

    const hubspotTimestampUpdates = new Map(
      normalizedRecords
        .filter((r) => r.hubspot_id != null && r.hs_lastmodifieddate)
        .map((r) => [r.hubspot_id.toString(), r.hs_lastmodifieddate])
    );

    if (hubspotTimestampUpdates.size > 0) {
      setHubspotData((prev) => prev.map((hubspotRecord) => {
        const hubspotId = hubspotRecord?.engagement?.id?.toString();
        const newTimestamp = hubspotId && hubspotTimestampUpdates.get(hubspotId);
        if (!newTimestamp) return hubspotRecord;

        return {
          ...hubspotRecord,
          properties: {
            ...(hubspotRecord.properties || {}),
            hs_lastmodifieddate: newTimestamp,
          },
        };
      }));
    }
  }, []);

  const mergeUpdatedRecord = useCallback((updatedRecord) => {
    pendingMergeQueueRef.current.push(updatedRecord);

    if (!mergeFlushScheduledRef.current) {
      mergeFlushScheduledRef.current = true;
      setTimeout(flushMergeQueue, 0);
    }
  }, [flushMergeQueue]);

  const performSyncRequest = useCallback(async (record, options = {}) => {
    const { showSuccessToast = false, showErrorModal = false } = options;

    try {
      // Send the already-loaded HubSpot data so the backend can sync from it directly
      // instead of doing another live HubSpot fetch (see ENGAGEMENT_PROPERTIES on the
      // backend for what's included). Falls back server-side to a live fetch if this
      // is missing or incomplete.
      const response = await fetch(`api/engagements/${record.hs_record.engagement.id}/sync_with_hubspot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({ hs_record: record.hs_record }),
      });

      if (response.ok) {
        const responseData = await response.json().catch(() => null);
        if (responseData?.data) {
          mergeUpdatedRecord(responseData.data);
        }

        if (showSuccessToast) {
          setSuccessToastMessage('The engagement was successfully synced with HubSpot.');
        }

        return { success: true };
      } else {
        const errorData = await response.json().catch(() => null);
        const message = errorData?.errors?.join('\n') || `Sync failed (HTTP ${response.status})`;

        if (showErrorModal) {
          setModalMessage(message);
        }

        return { success: false, message };
      }
    } catch (error) {
      console.error('Sync error:', error);
      const message = 'An unexpected error occurred while syncing.';

      if (showErrorModal) {
        setModalMessage(message);
      }

      return { success: false, message };
    }
  }, [mergeUpdatedRecord]);

  const sendSyncRequest = useCallback(async (record) => {
    if (bulkSyncRunningRef.current) return null;

    const recordKey = getRecordKey(record);
    if (activeSyncKeysRef.current.has(recordKey)) return null;

    activeSyncKeysRef.current.add(recordKey);
    setSyncingRecordIds((prev) => (prev.includes(recordKey) ? prev : [...prev, recordKey]));

    try {
      return await performSyncRequest(record, { showSuccessToast: true, showErrorModal: true });
    } finally {
      activeSyncKeysRef.current.delete(recordKey);
      setSyncingRecordIds((prev) => prev.filter((id) => id !== recordKey));
    }
  }, [getRecordKey, performSyncRequest]);

  const openSyncPreview = useCallback(async (record) => {
    if (bulkSyncRunningRef.current) return;

    setSyncPreview({
      isOpen: true,
      isLoading: true,
      isSaving: false,
      record,
      fields: null,
      isNewRecord: false,
      error: null,
    });

    try {
      const response = await fetch(`api/engagements/${record.hs_record.engagement.id}/sync_preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({ hs_record: record.hs_record }),
      });

      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        const message = responseData?.errors?.join('\n') || `Failed to load preview (HTTP ${response.status})`;
        setSyncPreview((prev) => ({ ...prev, isLoading: false, error: message }));
        return;
      }

      setSyncPreview((prev) => ({
        ...prev,
        isLoading: false,
        fields: responseData?.fields || [],
        isNewRecord: Boolean(responseData?.is_new_record),
      }));
    } catch (error) {
      console.error('Sync preview error:', error);
      setSyncPreview((prev) => ({
        ...prev,
        isLoading: false,
        error: 'An unexpected error occurred while loading the preview.',
      }));
    }
  }, []);

  const closeSyncPreview = useCallback(() => {
    setSyncPreview((prev) => (prev.isSaving ? prev : {
      isOpen: false, isLoading: false, isSaving: false, record: null, fields: null, isNewRecord: false, error: null,
    }));
  }, []);

  const confirmSyncPreview = useCallback(async () => {
    const record = syncPreview.record;
    if (!record) return;

    setSyncPreview((prev) => ({ ...prev, isSaving: true }));
    await sendSyncRequest(record);
    flushMergeQueue();
    setSyncPreview({
      isOpen: false, isLoading: false, isSaving: false, record: null, fields: null, isNewRecord: false, error: null,
    });
  }, [syncPreview.record, sendSyncRequest, flushMergeQueue]);

  const sendLeadSyncRequest = useCallback(async (dealId) => {
    if (bulkSyncRunningRef.current || !dealId) return null;

    try {
      const response = await fetch(`api/leads/${dealId}/sync_with_hubspot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
      });

      if (response.ok) {
        const responseData = await response.json().catch(() => null);
        if (responseData?.data) {
          const updatedLead = typeof responseData.data === 'string'
            ? JSON.parse(responseData.data)
            : responseData.data;
          const updatedLeadHubspotId = updatedLead.hubspot_id?.toString() || dealId?.toString();

          if (updatedLeadHubspotId) {
            setLeadLookup((prev) => ({
              ...prev,
              [updatedLeadHubspotId]: updatedLead,
            }));
          }
        }

        setSuccessToastMessage('The lead was successfully synced with HubSpot.');
      } else {
        const errorData = await response.json().catch(() => null);
        const message = errorData?.errors?.join('\n') || `Lead sync failed (HTTP ${response.status})`;
        setModalMessage(message);
      }
    } catch (error) {
      console.error('Lead sync error:', error);
      setModalMessage('An unexpected error occurred while syncing the lead.');
    }
  }, []);

  const closeBulkSyncModal = useCallback(() => {
    setBulkSyncState((prev) => {
      if (prev.isRunning) return prev;

      return {
        ...prev,
        isOpen: false,
        currentLabel: null,
      };
    });
  }, []);

  const openBulkSyncConfirmation = useCallback((records) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    setBulkSyncConfirmation({
      isOpen: true,
      records,
    });
  }, []);

  const closeBulkSyncConfirmation = useCallback(() => {
    setBulkSyncConfirmation({
      isOpen: false,
      records: [],
    });
  }, []);

  const startBulkSync = useCallback(async (records) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    bulkSyncRunningRef.current = true;
    setBulkSyncState({
      isOpen: true,
      isRunning: true,
      total: records.length,
      completed: 0,
      currentLabel: null,
      failures: [],
    });

    let failureCount = 0;
    let completedCount = 0;

    const syncOne = async (record) => {
      const recordKey = getRecordKey(record);
      const recordLabel = getRecordLabel(record);

      activeSyncKeysRef.current.add(recordKey);
      setSyncingRecordIds((prev) => (prev.includes(recordKey) ? prev : [...prev, recordKey]));
      setBulkSyncState((prev) => ({
        ...prev,
        currentLabel: recordLabel,
      }));

      const result = await performSyncRequest(record);

      if (!result?.success) {
        failureCount += 1;
        setBulkSyncState((prev) => ({
          ...prev,
          failures: [...prev.failures, {
            id: recordKey,
            label: recordLabel,
            message: result?.message || 'Unknown sync failure.',
          }],
        }));
      }

      activeSyncKeysRef.current.delete(recordKey);
      setSyncingRecordIds((prev) => prev.filter((id) => id !== recordKey));
      completedCount += 1;
      setBulkSyncState((prev) => ({
        ...prev,
        completed: completedCount,
      }));
    };

    try {
      // Several records sync concurrently (see BULK_SYNC_CONCURRENCY) instead of one
      // at a time — safe now that each request syncs from already-loaded data instead
      // of making its own live HubSpot call.
      await runWithConcurrency(records, BULK_SYNC_CONCURRENCY, syncOne);
    } finally {
      flushMergeQueue();
      bulkSyncRunningRef.current = false;
      setBulkSyncState((prev) => ({
        ...prev,
        isRunning: false,
        currentLabel: null,
      }));
      setSuccessToastMessage(`Bulk sync finished. ${records.length - failureCount} succeeded, ${failureCount} failed.`);
    }
  }, [flushMergeQueue, getRecordKey, getRecordLabel, performSyncRequest]);

  const confirmBulkSync = useCallback(async () => {
    const records = bulkSyncConfirmation.records;
    closeBulkSyncConfirmation();
    await startBulkSync(records);
  }, [bulkSyncConfirmation.records, closeBulkSyncConfirmation, startBulkSync]);

  const openBulkTimestampSyncConfirmation = useCallback((records, options = {}) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    setBulkTimestampSyncConfirmation({
      isOpen: true,
      records,
      endpoint: options.endpoint || DEFAULT_TIMESTAMP_SYNC_ENDPOINT,
      contextLabel: options.contextLabel || 'from the current page',
    });
  }, []);

  const closeBulkTimestampSyncConfirmation = useCallback(() => {
    setBulkTimestampSyncConfirmation({
      isOpen: false,
      records: [],
      endpoint: DEFAULT_TIMESTAMP_SYNC_ENDPOINT,
      contextLabel: 'from the current page',
    });
  }, []);

  // Merges successful { hubspot_id, hs_lastmodifieddate } results (from either
  // timestamp-sync endpoint) into ffData/hubspotData so the table reflects the new
  // HubSpot timestamp without a full reload. Shared by startBulkTimestampSync and
  // startSharedTimestampMassSync so both apply results the same way.
  const applyTimestampSyncResults = useCallback((results) => {
    const succeeded = results.filter((result) => result.success);
    const timestampByHubspotId = new Map(
      succeeded.map((result) => [result.hubspot_id?.toString(), result.hs_lastmodifieddate])
    );

    if (timestampByHubspotId.size === 0) return succeeded;

    setFFData((prev) => prev.map((engagement) => {
      const newTimestamp = timestampByHubspotId.get(engagement.hubspot_id?.toString());
      return newTimestamp ? { ...engagement, hs_lastmodifieddate: newTimestamp } : engagement;
    }));

    setHubspotData((prev) => prev.map((hubspotRecord) => {
      const newTimestamp = timestampByHubspotId.get(hubspotRecord.engagement?.id?.toString());
      if (!newTimestamp) return hubspotRecord;

      return {
        ...hubspotRecord,
        properties: { ...(hubspotRecord.properties || {}), hs_lastmodifieddate: newTimestamp },
      };
    }));

    return succeeded;
  }, []);

  // POSTs one batch of cached hs_records to a timestamp-sync endpoint and returns
  // the parsed { results, succeeded, failed } — or an errorMessage if the request
  // itself failed (as opposed to individual records being skipped, which comes
  // back as a normal result with success: false). Shared by startBulkTimestampSync
  // and startSharedTimestampMassSync.
  const postTimestampSync = useCallback(async (endpoint, records) => {
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({ hs_records: records.map((record) => record.hs_record) }),
      });

      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        return { errorMessage: responseData?.errors?.join('\n') || `Timestamp sync failed (HTTP ${response.status})` };
      }

      const results = responseData?.results || [];
      return {
        results,
        succeeded: results.filter((result) => result.success),
        failed: results.filter((result) => !result.success),
      };
    } catch (error) {
      console.error('Timestamp sync error:', error);
      return { errorMessage: 'An unexpected error occurred while syncing timestamps.' };
    }
  }, []);

  // Sends every record's already-loaded hs_record in one request instead of one
  // request per record. The backend re-verifies (server-side) that each record is
  // still timestamp-only before writing anything, and — unlike #sync_with_hubspot —
  // touches only the hs_lastmodifieddate column, not Engagement::Create/Update's full
  // field set, so this can't create/reassign a deal association. `endpoint` picks
  // between the general per-record backend (DEFAULT_TIMESTAMP_SYNC_ENDPOINT) and the
  // batched same-timestamp backend (SHARED_TIMESTAMP_SYNC_ENDPOINT) — both return the
  // same { results: [{ hubspot_id, success, hs_lastmodifieddate | error }] } shape.
  const startBulkTimestampSync = useCallback(async (records, endpoint = DEFAULT_TIMESTAMP_SYNC_ENDPOINT) => {
    if (bulkSyncRunningRef.current || records.length === 0) return;

    bulkSyncRunningRef.current = true;
    setBulkTimestampSyncState({ isRunning: true });

    try {
      const { results, succeeded, failed, errorMessage } = await postTimestampSync(endpoint, records);

      if (errorMessage) {
        setModalMessage(errorMessage);
        return;
      }

      applyTimestampSyncResults(results);

      if (failed.length > 0) {
        setModalMessage(
          `${succeeded.length} timestamp(s) updated. ${failed.length} were skipped:\n` +
          failed.map((result) => `#${result.hubspot_id}: ${result.error}`).join('\n')
        );
      } else {
        setSuccessToastMessage(`${succeeded.length} timestamp(s) synced with HubSpot.`);
      }
    } finally {
      bulkSyncRunningRef.current = false;
      setBulkTimestampSyncState({ isRunning: false });
    }
  }, [applyTimestampSyncResults, postTimestampSync]);

  const confirmBulkTimestampSync = useCallback(async () => {
    const { records, endpoint } = bulkTimestampSyncConfirmation;
    closeBulkTimestampSyncConfirmation();
    await startBulkTimestampSync(records, endpoint);
  }, [bulkTimestampSyncConfirmation, closeBulkTimestampSyncConfirmation, startBulkTimestampSync]);

  const openSharedTimestampMassSyncConfirmation = useCallback((groups) => {
    if (bulkSyncRunningRef.current || groups.length === 0) return;

    setSharedTimestampMassSyncConfirmation({ isOpen: true, groups });
  }, []);

  const closeSharedTimestampMassSyncConfirmation = useCallback(() => {
    setSharedTimestampMassSyncConfirmation({ isOpen: false, groups: [] });
  }, []);

  // Fires one bulk_sync_shared_timestamp request per group (up to
  // MAX_MASS_SYNC_GROUPS groups), a few at a time (see BULK_SYNC_CONCURRENCY —
  // capped low because this app's Puma config runs only 5 threads total, shared
  // by every user). Each group's request/response is recorded as its own row in
  // sharedTimestampMassSyncState.results for the progress modal, mirroring how
  // startBulkSync records one failure per record.
  const startSharedTimestampMassSync = useCallback(async (groups) => {
    if (bulkSyncRunningRef.current || groups.length === 0) return;

    bulkSyncRunningRef.current = true;
    setSharedTimestampMassSyncState({
      isOpen: true,
      isRunning: true,
      totalGroups: groups.length,
      completedGroups: 0,
      currentLabel: null,
      results: [],
    });

    let completedCount = 0;

    const syncGroup = async (group) => {
      setSharedTimestampMassSyncState((prev) => ({
        ...prev,
        currentLabel: formatDate(group.timestamp),
      }));

      const { results, succeeded, failed, errorMessage } = await postTimestampSync(SHARED_TIMESTAMP_SYNC_ENDPOINT, group.records);

      const resultRow = errorMessage
        ? {
          timestamp: group.timestamp,
          recordCount: group.records.length,
          succeeded: 0,
          failed: group.records.length,
          requestError: errorMessage,
          failures: [],
        }
        : {
          timestamp: group.timestamp,
          recordCount: group.records.length,
          succeeded: succeeded.length,
          failed: failed.length,
          requestError: null,
          failures: failed.map((result) => ({ hubspot_id: result.hubspot_id, error: result.error })),
        };

      if (!errorMessage) applyTimestampSyncResults(results);

      completedCount += 1;
      setSharedTimestampMassSyncState((prev) => ({
        ...prev,
        completedGroups: completedCount,
        results: [...prev.results, resultRow],
      }));
    };

    try {
      await runWithConcurrency(groups, BULK_SYNC_CONCURRENCY, syncGroup);
    } finally {
      bulkSyncRunningRef.current = false;
      setSharedTimestampMassSyncState((prev) => ({
        ...prev,
        isRunning: false,
        currentLabel: null,
      }));
    }
  }, [applyTimestampSyncResults, postTimestampSync]);

  const confirmSharedTimestampMassSync = useCallback(async () => {
    const { groups } = sharedTimestampMassSyncConfirmation;
    closeSharedTimestampMassSyncConfirmation();
    await startSharedTimestampMassSync(groups);
  }, [sharedTimestampMassSyncConfirmation, closeSharedTimestampMassSyncConfirmation, startSharedTimestampMassSync]);

  const closeSharedTimestampMassSyncModal = useCallback(() => {
    setSharedTimestampMassSyncState((prev) => (prev.isRunning ? prev : {
      isOpen: false,
      isRunning: false,
      totalGroups: 0,
      completedGroups: 0,
      currentLabel: null,
      results: [],
    }));
  }, []);

  // The async path only ever patches EngagementSyncBatch's own counters — it never
  // pushes updated hs_lastmodifieddate values back into ffData/hubspotData the way
  // applyTimestampSyncResults does for the synchronous sync buttons, so the page's
  // Sync Status badge/rows would otherwise stay stale until a manual reload. Call
  // this with whatever batch list a fetch just returned: for any batch actually
  // watched go from running to finished during this page load (not one that was
  // already finished before polling even started), bump reloadNonce once to
  // re-run the main engagements fetch and pick up the new statuses.
  const checkForNewlyFinishedBatches = useCallback((batches) => {
    (batches || []).forEach((batch) => {
      if (!batch.finished) {
        seenUnfinishedBatchIdsRef.current.add(batch.id);
        return;
      }

      if (seenUnfinishedBatchIdsRef.current.has(batch.id) && !notifiedFinishedBatchIdsRef.current.has(batch.id)) {
        notifiedFinishedBatchIdsRef.current.add(batch.id);
        setReloadNonce((prev) => prev + 1);
        setSuccessToastMessage(`Async sync batch #${batch.id} finished — refreshing engagement data...`);
      }
    });
  }, []);

  // Fetches EngagementSyncBatch summaries (id/counts/finished/percent_complete) —
  // lightweight, so it's safe to poll frequently without pulling every batch's
  // per-group results along with it.
  const fetchAsyncBatches = useCallback(async () => {
    try {
      const response = await fetch('api/engagements/async_sync_batches');
      if (!response.ok) return;

      const data = await response.json();
      const batches = data?.batches || [];
      setAsyncBatches(batches);
      checkForNewlyFinishedBatches(batches);
    } catch (error) {
      console.error('Failed to load async sync batches:', error);
    }
  }, [checkForNewlyFinishedBatches]);

  // Fetches one batch's per-group results (see EngagementSyncGroupResult) — only
  // called for the batch currently expanded in the progress panel, not for every
  // batch on every poll.
  const fetchAsyncBatchDetail = useCallback(async (batchId) => {
    try {
      const response = await fetch(`api/engagements/async_sync_batches/${batchId}`);
      if (!response.ok) return;

      const data = await response.json();
      if (data?.batch) {
        setAsyncBatches((prev) => {
          const others = prev.filter((batch) => batch.id !== data.batch.id);
          return [data.batch, ...others].sort((a, b) => b.id - a.id);
        });
        checkForNewlyFinishedBatches([data.batch]);
      }
      setAsyncBatchResultsById((prev) => ({ ...prev, [batchId]: data?.results || [] }));
    } catch (error) {
      console.error('Failed to load async sync batch detail:', error);
    }
  }, [checkForNewlyFinishedBatches]);

  const toggleAsyncBatchDetails = useCallback((batchId) => {
    setExpandedAsyncBatchId((prev) => {
      const next = prev === batchId ? null : batchId;
      if (next) fetchAsyncBatchDetail(next);
      return next;
    });
  }, [fetchAsyncBatchDetail]);

  const openAsyncGroupSyncConfirmation = useCallback((groups) => {
    if (bulkSyncRunningRef.current || groups.length === 0) return;

    setAsyncGroupSyncConfirmation({ isOpen: true, groups });
  }, []);

  const closeAsyncGroupSyncConfirmation = useCallback(() => {
    setAsyncGroupSyncConfirmation({ isOpen: false, groups: [] });
  }, []);

  // Hands every group (with its already-loaded hs_records) to background jobs in
  // one request instead of syncing them from the browser — the controller enqueues
  // one SharedTimestampGroupSyncJob per group on the backend. Unlike startSharedTimestampMassSync, this
  // doesn't wait for or reflect individual group results here — the job re-verifies
  // and applies each record server-side on its own schedule.
  const startAsyncGroupSync = useCallback(async (groups) => {
    if (groups.length === 0) return;

    setAsyncGroupSyncState({ isSubmitting: true });

    try {
      const response = await fetch(SHARED_TIMESTAMP_ASYNC_SYNC_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({
          groups: groups.map((group) => ({
            timestamp: group.timestamp,
            hs_records: group.records.map((record) => record.hs_record),
          })),
        }),
      });

      const responseData = await response.json().catch(() => null);

      if (!response.ok) {
        const message = responseData?.errors?.join('\n') || `Failed to queue async sync (HTTP ${response.status})`;
        setModalMessage(message);
        return;
      }

      setSuccessToastMessage(
        `Queued ${responseData?.group_count ?? groups.length} group(s) (${responseData?.record_count ?? 'all'} records) for background sync.`
      );

      // Show progress for this batch immediately instead of waiting for the next
      // poll tick — see the progress panel rendered from asyncBatches below.
      if (responseData?.batch_id != null) {
        setExpandedAsyncBatchId(responseData.batch_id);
        await fetchAsyncBatchDetail(responseData.batch_id);
      }
      await fetchAsyncBatches();
    } catch (error) {
      console.error('Async group sync error:', error);
      setModalMessage('An unexpected error occurred while queueing the async sync.');
    } finally {
      setAsyncGroupSyncState({ isSubmitting: false });
    }
  }, [fetchAsyncBatchDetail, fetchAsyncBatches]);

  const confirmAsyncGroupSync = useCallback(async () => {
    const { groups } = asyncGroupSyncConfirmation;
    closeAsyncGroupSyncConfirmation();
    await startAsyncGroupSync(groups);
  }, [asyncGroupSyncConfirmation, closeAsyncGroupSyncConfirmation, startAsyncGroupSync]);

  // Loads whatever async batches already exist (e.g. from before a reload) so
  // progress is visible without having to click "Sync All Groups Async" again.
  useEffect(() => {
    fetchAsyncBatches();
  }, [fetchAsyncBatches]);

  // Polls while any known batch is still running, or while one is expanded (so its
  // per-group results keep updating even after its summary reports finished: true
  // from a slightly earlier poll). Stops entirely once nothing is left to watch.
  useEffect(() => {
    const hasUnfinishedBatch = asyncBatches.some((batch) => !batch.finished);
    if (!hasUnfinishedBatch && !expandedAsyncBatchId) return undefined;

    const intervalId = window.setInterval(() => {
      fetchAsyncBatches();
      if (expandedAsyncBatchId) fetchAsyncBatchDetail(expandedAsyncBatchId);
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [asyncBatches, expandedAsyncBatchId, fetchAsyncBatches, fetchAsyncBatchDetail]);

  // Batches still running that were also queued today — the progress panel only
  // surfaces these, so a stale unfinished batch from a previous day (e.g. one that
  // never got marked finished) doesn't linger in the list indefinitely.
  const pendingAsyncBatchesToday = React.useMemo(
    () => asyncBatches.filter(
      (batch) => !batch.finished && DateTime.fromISO(batch.created_at).hasSame(DateTime.now(), 'day')
    ),
    [asyncBatches]
  );

  const getURLParams = () => new URLSearchParams(window.location.search);

  const getStartDate = () =>
    getURLParams().get('start_date') || DateTime.now().startOf('day').toISO().slice(0, 16);

  const getEndDate = () =>
    getURLParams().get('end_date') || DateTime.now().startOf('day').plus({ days: 1 }).toISO().slice(0, 16);

  // The backend still receives full start_date/end_date datetime params (start of
  // day / start of next day) — this just derives a single day value for the picker.
  const getSelectedDate = () => DateTime.fromISO(getStartDate()).toFormat('yyyy-MM-dd');

  const setDateParams = (start, end) => {
    const params = new URLSearchParams(window.location.search);
    params.set('start_date', start);
    params.set('end_date', end);
    window.history.pushState({}, '', `?${params.toString()}`);
    setFilter(start + end);
  };

  const [filter, setFilter] = useState(getStartDate() + getEndDate());

  useEffect(() => {
    if (!successToastMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setSuccessToastMessage(null);
    }, 3500);

    return () => window.clearTimeout(timeoutId);
  }, [successToastMessage]);

  // Initialize URL params on first load if missing
  useEffect(() => {
    const params = getURLParams();
    if (!params.get('start_date') || !params.get('end_date')) {
      setDateParams(getStartDate(), getEndDate());
    }
  }, []);

  useEffect(() => {
    setLoadingData(true);
    setLoadError(null);
    setFFData([]);
    setLeadLookup({});
    setHubspotData([]);
    setLoadingProgress(null);
    setLoadDurationSeconds(null);
    setRequestLog([]);

    const startTime = Date.now();
    let cancelled = false;

    async function fetchAllPages() {
      const accumulatedHsData = [];
      const accumulatedFFData = [];
      const accumulatedLeads = {};
      const log = [];
      let scanned = 0;
      let offset = null;
      let hasMore = true;
      let requestNumber = 0;

      while (hasMore) {
        if (cancelled) break;

        const searchParams = new URLSearchParams({
          start_date: getStartDate(),
          end_date: getEndDate(),
        });

        if (offset) {
          searchParams.set('offset', offset);
        }

        requestNumber += 1;
        const currentRequestNumber = requestNumber;
        const requestStartedAt = Date.now();

        log.push({
          requestNumber: currentRequestNumber,
          offset: offset || 0,
          chunkIndex: null,
          totalChunks: null,
          engagementType: null,
          bucketStart: null,
          bucketEnd: null,
          status: null,
          ok: null,
          pending: true,
          durationMs: null,
          scanned: null,
          returned: null,
          error: null,
        });
        if (!cancelled) setRequestLog([...log]);

        let response = null;
        let res = null;
        let errorMessage = null;

        try {
          response = await fetch(`api/engagements?${searchParams.toString()}`);
          res = await response.json();
          if (!response.ok) {
            errorMessage = `HTTP ${response.status}`;
          }
        } catch (err) {
          errorMessage = err.message;
        }

        const entryIndex = log.findIndex((entry) => entry.requestNumber === currentRequestNumber);
        if (entryIndex !== -1) {
          log[entryIndex] = {
            requestNumber: currentRequestNumber,
            offset: offset || 0,
            chunkIndex: res?.meta?.chunk_index,
            totalChunks: res?.meta?.total_chunks,
            engagementType: res?.meta?.engagement_type,
            bucketStart: res?.meta?.bucket_start,
            bucketEnd: res?.meta?.bucket_end,
            status: response ? response.status : null,
            ok: !errorMessage,
            pending: false,
            durationMs: Date.now() - requestStartedAt,
            scanned: res?.meta?.scanned_count,
            returned: res?.meta?.returned,
            error: errorMessage,
          };
        }
        if (!cancelled) setRequestLog([...log]);

        if (errorMessage) {
          throw new Error(`Failed to load engagements (${errorMessage})`);
        }

        // Each request only covers one (type, date-bucket) chunk, so ff_data/leads
        // are scoped to that chunk too — accumulate across every page, not just the first.
        accumulatedHsData.push(...(res.data || []));
        accumulatedFFData.push(...(res.ff_data || []));
        Object.assign(accumulatedLeads, res.leads || {});
        scanned += res.meta?.scanned_count || 0;
        offset = res.meta.next_offset;

        if (!cancelled) {
          setHubspotData([...accumulatedHsData]);
          setFFData([...accumulatedFFData]);
          setLeadLookup({ ...accumulatedLeads });
          setLoadingProgress({ loaded: scanned, matched: accumulatedHsData.length });
        }

        hasMore = Boolean(res.meta?.has_more && offset);
      }
    }

    fetchAllPages()
      .then(() => {
        if (!cancelled) {
          setLoadDurationSeconds(((Date.now() - startTime) / 1000).toFixed(2));
          setLoadingData(false);
          setLoadingProgress(null);
        }
      })
      .catch((error) => {
        console.error(error);

        if (!cancelled) {
          setLoadError('Unable to load engagement records for the selected time window.');
          setLoadDurationSeconds(((Date.now() - startTime) / 1000).toFixed(2));
          setLoadingData(false);
          setLoadingProgress(null);
        }
      });

    return () => { cancelled = true; };
  }, [filter, reloadNonce]);

  const humanizeDiffKey = (key) => key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  const formatDiffValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-muted">—</span>;
    }
    if (Array.isArray(value)) {
      return value.length ? value.join(', ') : <span className="text-muted">—</span>;
    }
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  };

  const calculateSyncStatus = (ff_record, hs_record) => {
    if (!hs_record && ff_record) return 'ff_only';
    if (hs_record && !ff_record) return 'hs_only';
    const hs_value = formatDate(hs_record?.properties?.hs_lastmodifieddate);
    const ff_value = formatDate(ff_record?.hs_lastmodifieddate);
    if (hs_value === ff_value) return 'synced';
    // Set server-side (see flag_timestamp_only_diff): true when hs_lastmodifieddate
    // is the only thing that changed — HubSpot bumps it for lots of things we don't
    // track (call disposition, owner reassignment, ...), so this isn't necessarily
    // worth acting on the way a real content/deal change is.
    if (hs_record?.resolved?.timestamp_only_diff) return 'out_of_sync_timestamp_only';
    return 'out_of_sync';
  };

  // Keyed by HubSpot engagement id so ffDataWithHsRecord below is O(n) instead of
  // O(n*m) — with thousands of loaded records, a .find() per ffData row (the
  // previous approach) made every state update (e.g. after a single-record sync)
  // take several seconds to re-render.
  const hubspotDataById = React.useMemo(() => {
    const map = new Map();
    hubspotData.forEach((hs) => {
      const id = hs.engagement?.id?.toString();
      if (id) map.set(id, hs);
    });
    return map;
  }, [hubspotData]);

  const ffDataWithHsRecord = React.useMemo(() => ffData.map((engagement) => {
    const matchingHubspotData = hubspotDataById.get(engagement.hubspot_id?.toString());
    return {
      ff_record: engagement,
      hs_record: matchingHubspotData,
      sync_status: calculateSyncStatus(engagement, matchingHubspotData),
    };
  }), [ffData, hubspotDataById]);

  const combinedData = React.useMemo(() => {
    const matchingHsIds = new Set(ffData.map((r) => r.hubspot_id?.toString()));
    const unmatchedHubspotData = hubspotData.filter(
      (hs) => !matchingHsIds.has(hs.engagement.id?.toString())
    );
    const formattedUnmatchedHubspotData = unmatchedHubspotData.map((hs) => ({
      ff_record: null,
      hs_record: hs,
      sync_status: 'hs_only',
    }));

    return [...formattedUnmatchedHubspotData, ...ffDataWithHsRecord].map((row) => {
      const dealIds = row.hs_record?.associations?.dealIds || row.hs_record?.associations?.deal_ids || [];
      const matchingLeads = dealIds
        .map((dealId) => leadLookup[dealId?.toString()])
        .filter(Boolean);

      return {
        ...row,
        hs_deal_ids: dealIds,
        matching_leads: matchingLeads,
        has_matching_lead: matchingLeads.length > 0,
      };
    });
  }, [ffDataWithHsRecord, ffData, hubspotData, leadLookup]);

  const statusCounts = React.useMemo(() => {
    return combinedData.reduce((acc, row) => {
      acc[row.sync_status] = (acc[row.sync_status] || 0) + 1;
      return acc;
    }, {});
  }, [combinedData]);

  // Groups current out_of_sync_timestamp_only records by their exact HS Version
  // Timestamp — a HubSpot bulk operation (a workflow run, a batch property update)
  // stamps many unrelated engagements with the identical millisecond, so several
  // records sharing one timestamp is a signal worth surfacing and letting an admin
  // filter down to, rather than something visible only by scrolling the table.
  const sharedTimestampGroups = React.useMemo(() => {
    const groups = new Map();

    combinedData.forEach((row) => {
      if (row.sync_status !== 'out_of_sync_timestamp_only') return;

      const timestamp = row.hs_record?.properties?.hs_lastmodifieddate;
      if (!timestamp) return;

      if (!groups.has(timestamp)) groups.set(timestamp, []);
      groups.get(timestamp).push(row);
    });

    return Array.from(groups.entries())
      .filter(([, records]) => records.length > 1)
      .map(([timestamp, records]) => ({ timestamp, records }))
      .sort((a, b) => b.records.length - a.records.length || a.timestamp.localeCompare(b.timestamp));
  }, [combinedData]);

  // Candidate groups for "Sync Top N Groups": the largest MAX_MASS_SYNC_GROUPS_EXTENDED
  // groups (sharedTimestampGroups is already sorted largest-first), narrowed to
  // each group's syncable records, dropping any group left with none. The Top 10
  // button's list is just the first 10 of this, so both buttons stay in sync off
  // one computation.
  const massSyncCandidateGroupsExtended = React.useMemo(() => {
    return sharedTimestampGroups
      .slice(0, MAX_MASS_SYNC_GROUPS_EXTENDED)
      .map((group) => ({ ...group, records: group.records.filter(canSyncRecord) }))
      .filter((group) => group.records.length > 0);
  }, [sharedTimestampGroups, canSyncRecord]);
  const massSyncCandidateGroups = React.useMemo(
    () => massSyncCandidateGroupsExtended.slice(0, MAX_MASS_SYNC_GROUPS),
    [massSyncCandidateGroupsExtended]
  );
  // Unlike massSyncCandidateGroups/-Extended, "Sync All Groups Async" isn't capped —
  // the whole point of queuing groups into background jobs instead of syncing them
  // from the browser is to handle a day with more shared-timestamp groups than a
  // live request/click could reasonably wait on.
  const massSyncCandidateGroupsAll = React.useMemo(() => {
    return sharedTimestampGroups
      .map((group) => ({ ...group, records: group.records.filter(canSyncRecord) }))
      .filter((group) => group.records.length > 0);
  }, [sharedTimestampGroups, canSyncRecord]);
  const bulkProgressPercent = bulkSyncState.total === 0
    ? 0
    : Math.round((bulkSyncState.completed / bulkSyncState.total) * 100);
  const massSyncProgressPercent = sharedTimestampMassSyncState.totalGroups === 0
    ? 0
    : Math.round((sharedTimestampMassSyncState.completedGroups / sharedTimestampMassSyncState.totalGroups) * 100);

  const columns = React.useMemo(
    () => [
      {
        accessorFn: (row) => row.ff_record?.id?.toString() || '',
        header: 'FF ID',
        cell: (info) => <div style={{ minWidth: '70px' }}>{info.getValue()}</div>,
      },
      {
        accessorFn: (row) => row.hs_record?.engagement?.id?.toString() || '',
        header: 'HS ID',
      },
      {
        id: 'hs_version_timestamp',
        // Raw ISO string here (not formatDate's output) so sorting is chronological —
        // formatDate uses 12-hour "hh a" which sorts wrong as a string (e.g. "01:00 PM"
        // sorts before "11:00 AM"). Formatting is applied only for display, in `cell`.
        accessorFn: (row) => row.hs_record?.properties?.hs_lastmodifieddate,
        cell: (info) => formatDate(info.getValue()),
        header: 'HS Version Timestamp',
        // Exact match, same reasoning as sync_status below — substring matching
        // (the default) would make one shared-timestamp filter also match other
        // timestamps that happen to contain it as a substring.
        filterFn: 'equalsString',
      },
      {
        id: 'sync_status',
        accessorFn: (row) => calculateSyncStatus(row.ff_record, row.hs_record),
        header: 'Sync Status',
        // Exact match — the default filter fn does substring matching, which made
        // "out_of_sync" also match "out_of_sync_timestamp_only".
        filterFn: 'equalsString',
        meta: { filterVariant: 'select' },
      },
      {
        accessorFn: (row) => row.hs_record?.engagement?.type,
        header: 'Engagement Type',
        filterFn: 'equalsString',
        meta: { filterVariant: 'select' },
      },
      {
        accessorFn: (row) => formatDate(row.ff_record?.hs_lastmodifieddate),
        header: 'FF Version Timestamp',
      },
            {
                    accessorFn: (row) => [...new Set((row.matching_leads || []).map((lead) => lead.id))].join('; '),
        header: 'FF Lead ID',
      },
      {
              accessorFn: (row) => (row.hs_deal_ids || []).join('; '),
        header: 'HS Deal ID',
      },
      {
        accessorFn: (row) => row.hs_record?.resolved?.deal_names?.join('; ') ?? '',
        header: 'HS Deal Names',
      },
      {
        accessorFn: (row) => row.hs_record?.resolved?.pipeline_names?.join('; ') ?? '',
        header: 'Pipeline Name',
      },
      {
        accessorFn: (row) => row.hs_record?.resolved?.contact_names?.join('; ') ?? '',
        header: 'HS Contact Names',
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const record = info.row.original;
          const dealIds = record.hs_record?.associations?.dealIds || [];
          const isRowSyncing = syncingRecordIds.includes(getRecordKey(record));
          const shouldStack = dealIds.length > 1;

          return (
            <div
              style={{
                display: 'flex',
                flexDirection: shouldStack ? 'column' : 'row',
                gap: '4px',
                alignItems: shouldStack ? 'flex-end' : undefined,
                justifyContent: shouldStack ? undefined : 'flex-end',
              }}
            >
              {canSyncRecord(record) && (
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => openSyncPreview(record)}
                  disabled={bulkSyncState.isRunning || isRowSyncing}
                >
                  {isRowSyncing ? 'Syncing...' : 'Sync Record'}
                </button>
              )}
              {dealIds.map((dealId) => (
                <button
                  key={dealId}
                  className="btn btn-sm btn-outline-primary"
                  onClick={() => sendLeadSyncRequest(dealId)}
                  disabled={bulkSyncState.isRunning}
                >
                  Sync Lead
                </button>
              ))}
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setJsonModal(record)}>
                View JSON
              </button>
            </div>
          );
        },
      },
    ],
    [bulkSyncState.isRunning, canSyncRecord, getRecordKey, openSyncPreview, sendLeadSyncRequest, syncingRecordIds],
  );

  return (
    <>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
        <div style={{ flex: '1 1 0', border: '1px solid #ccc', padding: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <label htmlFor="selected-date" style={{ marginBottom: 0 }}>HS Updated At:</label>
            <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{ height: '30px' }}
                title="Previous day"
                aria-label="Previous day"
                onClick={() => {
                  const newStartDate = DateTime.fromISO(getStartDate()).minus({ days: 1 }).startOf('day').toISO().slice(0, 16);
                  const newEndDate = DateTime.fromISO(newStartDate).plus({ days: 1 }).toISO().slice(0, 16);
                  setDateParams(newStartDate, newEndDate);
                }}
              >
                &#8592;
              </button>
              <input
                id="selected-date"
                type="date"
                style={{ height: '30px' }}
                value={getSelectedDate()}
                onChange={(event) => {
                  if (!event.target.value) return;

                  const newStartDate = DateTime.fromISO(event.target.value).startOf('day').toISO().slice(0, 16);
                  const newEndDate = DateTime.fromISO(newStartDate).plus({ days: 1 }).toISO().slice(0, 16);
                  setDateParams(newStartDate, newEndDate);
                }}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                style={{ height: '30px' }}
                title="Next day"
                aria-label="Next day"
                onClick={() => {
                  const newStartDate = DateTime.fromISO(getStartDate()).plus({ days: 1 }).startOf('day').toISO().slice(0, 16);
                  const newEndDate = DateTime.fromISO(newStartDate).plus({ days: 1 }).toISO().slice(0, 16);
                  setDateParams(newStartDate, newEndDate);
                }}
              >
                &#8594;
              </button>
            </div>
          </div>
        </div>
        <div style={{ flex: '1 1 0', border: '1px solid #ccc', padding: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {requestLog.some((entry) => entry.pending) ? (
            <div style={{ padding: '4px 0', color: '#666', fontSize: '14px' }}>
              Loading...
            </div>
          ) : loadDurationSeconds ? (
            <>
              <div style={{ padding: '4px 0', color: '#666', fontSize: '14px' }}>
                Loaded in {loadDurationSeconds}s
              </div>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ height: '30px' }}
                onClick={() => setReloadNonce((prev) => prev + 1)}
              >
                Reload Data
              </button>
            </>
          ) : null}
        </div>
        <div style={{ flex: '1 1 0', border: '1px solid #ccc', padding: '10px' }}>
          {requestLog.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setRequestLogModalOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#0d6efd',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                Request log ({requestLog.length} request{requestLog.length === 1 ? '' : 's'})
              </button>
              <RequestLogBadge requestLog={requestLog} />
            </div>
          )}
        </div>
      </div>

      {requestLogModalOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    Request log ({requestLog.length} request{requestLog.length === 1 ? '' : 's'})
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setRequestLogModalOpen(false)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table-sm table-bordered" style={{ fontSize: '12px', marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Chunk</th>
                          <th>Type</th>
                          <th>Bucket range</th>
                          <th>Status</th>
                          <th>Duration</th>
                          <th>Scanned</th>
                          <th>Returned</th>
                        </tr>
                      </thead>
                      <tbody>
                        {requestLog.map((entry) => (
                          <tr
                            key={entry.requestNumber}
                            style={
                              entry.pending ? { backgroundColor: 'rgba(255, 193, 7, 0.1)' }
                              : entry.ok === false ? { backgroundColor: 'rgba(255, 0, 0, 0.08)' }
                              : undefined
                            }
                          >
                            <td>{entry.requestNumber}</td>
                            <td>{entry.chunkIndex != null ? `${entry.chunkIndex + 1}/${entry.totalChunks}` : entry.offset}</td>
                            <td>{entry.engagementType || '—'}</td>
                            <td>
                              {entry.bucketStart && entry.bucketEnd
                                ? `${DateTime.fromISO(entry.bucketStart).toFormat('MM/dd HH:mm')} – ${DateTime.fromISO(entry.bucketEnd).toFormat('MM/dd HH:mm')}`
                                : '—'}
                            </td>
                            <td>
                              {entry.pending
                                ? 'Pending...'
                                : entry.ok ? entry.status : `${entry.status || 'ERR'}${entry.error ? ` (${entry.error})` : ''}`}
                            </td>
                            <td>{entry.pending ? '—' : `${entry.durationMs}ms`}</td>
                            <td>{entry.scanned ?? '—'}</td>
                            <td>{entry.returned ?? '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setRequestLogModalOpen(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={() => setRequestLogModalOpen(false)} />
        </>
      )}

      <div className="border bg-white p-3">
        <div className="border" style={{ flexDirection: 'row', display: 'flex' }}>
          <div style={{ padding: '10px', width: '50%' }}>
            <strong>Sync Status:</strong> <span className="text-muted" style={{ fontSize: '12px' }}>(click a status to filter)</span>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('hs_only')}
              title="Filter to hs_only records"
            >
              <b className="border px-1" style={{ width: '50px', backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: '3px' }}>{statusCounts.hs_only || 0}</b>
              <b className="border px-1" style={{ backgroundColor: 'rgba(255, 0, 0, 0.1)', borderRadius: '3px' }}>hs_only</b> Record exists in Hubspot but not in Fastlane
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('ff_only')}
              title="Filter to ff_only records"
            >
              <b className="border px-1" style={{ width: '50px', backgroundColor: 'rgba(255, 255, 0, 0.1)', borderRadius: '3px' }}>{statusCounts.ff_only || 0}</b>
              <b className="border px-1" style={{ backgroundColor: 'rgba(255, 255, 0, 0.1)', borderRadius: '3px' }}>ff_only</b> Record exists in Fastlane but not in Hubspot
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('out_of_sync')}
              title="Filter to out_of_sync records"
            >
              <b className="border px-1" style={{ width: '50px', backgroundColor: '#f5f5f5', borderRadius: '3px' }}>{statusCounts.out_of_sync || 0}</b>
              <b className="border px-1" style={{ backgroundColor: '#f5f5f5', borderRadius: '3px' }}>out_of_sync</b> Record exists in both systems but has real changes (content or deal) beyond the timestamp
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('out_of_sync_timestamp_only')}
              title="Filter to out_of_sync_timestamp_only records"
            >
              <b className="border px-1" style={{ width: '50px', backgroundColor: 'rgba(0, 123, 255, 0.05)', borderRadius: '3px' }}>{statusCounts.out_of_sync_timestamp_only || 0}</b>
              <b className="border px-1" style={{ backgroundColor: 'rgba(0, 123, 255, 0.05)', borderRadius: '3px' }}>out_of_sync_timestamp_only</b> HubSpot's timestamp changed but nothing we track did — safe to ignore
            </div>
            <div
              style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '3px', cursor: 'pointer' }}
              onClick={() => toggleSyncStatusFilter('synced')}
              title="Filter to synced records"
            >
              <b className="border px-1" style={{ width: '50px', backgroundColor: 'white', borderRadius: '3px' }}>{statusCounts.synced || 0}</b>
              <b className="border px-1" style={{ backgroundColor: 'white', borderRadius: '3px' }}>synced</b> Record exists in both systems and is in sync
            </div>
          </div>
        </div>

        {activeSyncStatusFilter === 'out_of_sync_timestamp_only' && sharedTimestampGroups.length > 0 && (
          <div className="border mt-3" style={{ padding: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
              <div>
                <strong>Shared Timestamps:</strong>{' '}
                <span>({sharedTimestampGroups.length} group{sharedTimestampGroups.length === 1 ? '' : 's'})</span>{' '}
                <span className="text-muted" style={{ fontSize: '12px' }}>
                  out_of_sync_timestamp_only records sharing one exact HS Version Timestamp — often one HubSpot bulk operation. Click the timestamp to filter, or Sync to update just that group in one batched request.
                </span>
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ whiteSpace: 'nowrap' }}
                  disabled={sharedTimestampMassSyncState.isRunning || massSyncCandidateGroups.length === 0}
                  onClick={() => openSharedTimestampMassSyncConfirmation(massSyncCandidateGroups)}
                  title={`Sends one batched request per group (up to the ${MAX_MASS_SYNC_GROUPS} largest) and shows the result of each`}
                >
                  {sharedTimestampMassSyncState.isRunning
                    ? `Syncing (${sharedTimestampMassSyncState.completedGroups}/${sharedTimestampMassSyncState.totalGroups})`
                    : `Sync Top ${massSyncCandidateGroups.length} Group${massSyncCandidateGroups.length === 1 ? '' : 's'}`}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ whiteSpace: 'nowrap' }}
                  disabled={sharedTimestampMassSyncState.isRunning || massSyncCandidateGroupsExtended.length === 0}
                  onClick={() => openSharedTimestampMassSyncConfirmation(massSyncCandidateGroupsExtended)}
                  title={`Sends one batched request per group (up to the ${MAX_MASS_SYNC_GROUPS_EXTENDED} largest) and shows the result of each`}
                >
                  {sharedTimestampMassSyncState.isRunning
                    ? `Syncing (${sharedTimestampMassSyncState.completedGroups}/${sharedTimestampMassSyncState.totalGroups})`
                    : `Sync Top ${massSyncCandidateGroupsExtended.length} Group${massSyncCandidateGroupsExtended.length === 1 ? '' : 's'}`}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  style={{ whiteSpace: 'nowrap' }}
                  disabled={asyncGroupSyncState.isSubmitting || massSyncCandidateGroupsAll.length === 0}
                  onClick={() => openAsyncGroupSyncConfirmation(massSyncCandidateGroupsAll)}
                  title="Queues every group (no cap) into a background job — one child job per group — instead of syncing them from this page"
                >
                  {asyncGroupSyncState.isSubmitting
                    ? 'Queueing...'
                    : `Sync All ${massSyncCandidateGroupsAll.length} Group${massSyncCandidateGroupsAll.length === 1 ? '' : 's'} Async`}
                </button>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
                marginTop: '6px',
                overflow: 'hidden',
                maxHeight: sharedTimestampsExpanded ? 'none' : SHARED_TIMESTAMPS_COLLAPSED_HEIGHT,
              }}
            >
              {sharedTimestampGroups.map(({ timestamp, records }) => {
                const syncableRecords = records.filter(canSyncRecord);

                return (
                  <span
                    key={timestamp}
                    className="border"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      borderRadius: '3px',
                      backgroundColor: 'rgba(0, 123, 255, 0.05)',
                      padding: '2px 2px 2px 6px',
                    }}
                  >
                    <b
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleSharedTimestampFilter(timestamp)}
                      title={`Filter to out_of_sync_timestamp_only records with HS Version Timestamp ${formatDate(timestamp)}`}
                    >
                      {formatDate(timestamp)} ({records.length})
                    </b>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      style={{ padding: '0 6px', fontSize: '11px', lineHeight: '1.6' }}
                      disabled={bulkTimestampSyncState.isRunning || syncableRecords.length === 0}
                      onClick={() => openBulkTimestampSyncConfirmation(syncableRecords, {
                        endpoint: SHARED_TIMESTAMP_SYNC_ENDPOINT,
                        contextLabel: `sharing HS Version Timestamp ${formatDate(timestamp)}`,
                      })}
                      title="Update every record in this group in a single batched request, re-verified server-side first"
                    >
                      Sync
                    </button>
                  </span>
                );
              })}
            </div>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 mt-1"
              style={{ fontSize: '12px' }}
              onClick={() => setSharedTimestampsExpanded((prev) => !prev)}
            >
              {sharedTimestampsExpanded ? 'Show fewer' : `Show all ${sharedTimestampGroups.length} groups`}
            </button>
          </div>
        )}

        {pendingAsyncBatchesToday.length > 0 && (
          <div className="border mt-3" style={{ padding: '10px' }}>
            <strong>Async Group Sync Batches:</strong>{' '}
            <span className="text-muted" style={{ fontSize: '12px' }}>
              Progress for today's "Sync All Groups Async" runs still going — queued groups sync in the background (see SharedTimestampGroupSyncJob).
              This panel disappears once every run below has finished.
            </span>
            <div style={{ marginTop: '8px' }}>
              {pendingAsyncBatchesToday.map((batch) => {
                const results = asyncBatchResultsById[batch.id] || [];
                const isExpanded = expandedAsyncBatchId === batch.id;

                return (
                  <div key={batch.id} className="border-bottom pb-2 mb-2">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <div>
                        <strong>Batch #{batch.id}</strong>{' '}
                        <span className="text-muted" style={{ fontSize: '12px' }}>{formatDate(batch.created_at)}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span className={batch.failed_records > 0 ? 'text-danger' : 'text-success'}>
                          {batch.completed_groups}/{batch.total_groups} groups · {batch.succeeded_records} succeeded, {batch.failed_records} failed (of {batch.total_records})
                        </span>
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => toggleAsyncBatchDetails(batch.id)}
                        >
                          {isExpanded ? 'Hide Details' : 'Details'}
                        </button>
                      </div>
                    </div>
                    <div className="progress mt-1" style={{ height: '8px' }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${batch.percent_complete}%` }}
                        aria-valuenow={batch.percent_complete}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                    {!batch.finished && (
                      <div className="text-muted" style={{ fontSize: '12px', marginTop: '4px' }}>Running...</div>
                    )}
                    {isExpanded && (
                      <div
                        className="border rounded mt-2 p-2"
                        style={{ maxHeight: '220px', overflowY: 'auto', backgroundColor: '#fafafa' }}
                      >
                        {results.length === 0 ? (
                          <div className="text-muted">No group results yet.</div>
                        ) : (
                          results.map((result) => (
                            <div key={result.id} className="border-bottom pb-2 mb-2">
                              <div>
                                <strong>{formatDate(result.timestamp)}</strong>{' '}
                                <span className={result.failed_count > 0 || result.request_error ? 'text-danger' : 'text-success'}>
                                  {result.succeeded_count} succeeded, {result.failed_count} failed (of {result.record_count})
                                </span>
                              </div>
                              {result.request_error && (
                                <div style={{ whiteSpace: 'pre-line' }}>{result.request_error}</div>
                              )}
                              {(result.failures || []).length > 0 && (
                                <ul style={{ marginBottom: 0 }}>
                                  {result.failures.map((failure, index) => (
                                    <li key={`${result.id}-${failure.hubspot_id}-${index}`}>
                                      #{failure.hubspot_id}: {failure.error}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {loadingProgress && (
          <div style={{ padding: '8px 0', color: '#666', fontSize: '14px' }}>
            Loading HubSpot records... {loadingProgress.matched} matched ({loadingProgress.loaded} scanned)
          </div>
        )}

        {loadError ? (
          <div className="border rounded text-center" style={{ padding: '40px 20px', backgroundColor: '#fafafa' }}>
            <div className="text-danger mb-3">{loadError}</div>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => setReloadNonce((prev) => prev + 1)}
            >
              Retry
            </button>
          </div>
        ) : (
          <Table
            loadingFFData={loadingData}
            // Once loadingProgress has something to show (see above), it's more
            // specific than Table's generic fallback — suppress that so only one
            // loading message is visible at a time. Before the first chunk
            // response lands (loadingProgress is still null), the generic message
            // is the only indicator, so it stays.
            loadingMessage={loadingProgress ? null : undefined}
            data={combinedData}
            columns={columns}
            initialSorting={[{ id: 'hs_version_timestamp', desc: true }]}
            autoResetPageIndex={false}
            onFiltersChange={(filters) => {
              setActiveSyncStatusFilter(filters.find((filter) => filter.id === 'sync_status')?.value);
            }}
            renderToolbar={(table) => {
              tableInstanceRef.current = table;

              const syncStatusFilter = table.getColumn('sync_status')?.getFilterValue();
              const showSyncThisPage = syncStatusFilter === 'hs_only' || syncStatusFilter === 'out_of_sync';
              const showSyncTimestampsOnly = syncStatusFilter === 'out_of_sync_timestamp_only';
              // Every chunk request must have come back 200 before trusting the loaded
              // data enough to bulk-sync from it — loadError only gets set if a chunk
              // failed (see fetchAllPages), so this is "the whole page loaded cleanly."
              const loadedCleanly = !loadingData && !loadError;
              const disabledReason = !loadedCleanly
                ? 'Reload the page — the last data load did not fully succeed'
                : undefined;

              const pageSyncableRecords = table.getRowModel().rows
                .map((row) => row.original)
                .filter(canSyncRecord);

              const pageTimestampOnlyRecords = table.getRowModel().rows
                .map((row) => row.original)
                .filter((row) => row.sync_status === 'out_of_sync_timestamp_only' && canSyncRecord(row));

              return (
                <div style={{ display: 'flex', gap: '8px' }}>
                  {showSyncThisPage && (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      disabled={bulkSyncState.isRunning || pageSyncableRecords.length === 0 || !loadedCleanly}
                      onClick={() => openBulkSyncConfirmation(pageSyncableRecords)}
                      title={disabledReason}
                    >
                      {bulkSyncState.isRunning
                        ? `Syncing Page (${bulkSyncState.completed}/${bulkSyncState.total})`
                        : `Sync This Page (${pageSyncableRecords.length})`}
                    </button>
                  )}
                  {showSyncTimestampsOnly && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={bulkTimestampSyncState.isRunning || pageTimestampOnlyRecords.length === 0 || !loadedCleanly}
                      onClick={() => openBulkTimestampSyncConfirmation(pageTimestampOnlyRecords)}
                      title={disabledReason || 'Updates only the HubSpot timestamp, in a single request, for records where nothing this app tracks has actually changed'}
                    >
                      {bulkTimestampSyncState.isRunning
                        ? 'Syncing Timestamps...'
                        : `Sync Timestamps Only (${pageTimestampOnlyRecords.length})`}
                    </button>
                  )}
                </div>
              );
            }}
          />
        )}
      </div>

      {bulkSyncConfirmation.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Bulk Sync</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeBulkSyncConfirmation}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '12px' }}>
                    Sync {bulkSyncConfirmation.records.length} records from the current page?
                  </p>
                  <p className="text-muted" style={{ marginBottom: 0 }}>
                    This will send up to {BULK_SYNC_CONCURRENCY} requests at a time and open the progress modal immediately after you confirm.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeBulkSyncConfirmation}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={confirmBulkSync}>
                    Start Sync
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={closeBulkSyncConfirmation} />
        </>
      )}

      {bulkTimestampSyncConfirmation.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Timestamp Sync</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeBulkTimestampSyncConfirmation}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '12px' }}>
                    Update the HubSpot timestamp for {bulkTimestampSyncConfirmation.records.length} record{bulkTimestampSyncConfirmation.records.length === 1 ? '' : 's'} {bulkTimestampSyncConfirmation.contextLabel}?
                  </p>
                  <p className="text-muted" style={{ marginBottom: 0 }}>
                    This sends a single batch request and updates only the hs_lastmodifieddate column for each record — no other fields are touched. Each record is re-verified on the server before its timestamp is updated, and any record found to have real changes is skipped rather than synced.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeBulkTimestampSyncConfirmation}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={confirmBulkTimestampSync}>
                    Sync Timestamps
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={closeBulkTimestampSyncConfirmation} />
        </>
      )}

      {sharedTimestampMassSyncConfirmation.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Shared Timestamp Sync</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeSharedTimestampMassSyncConfirmation}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '12px' }}>
                    Sync the {sharedTimestampMassSyncConfirmation.groups.length} largest shared-timestamp group{sharedTimestampMassSyncConfirmation.groups.length === 1 ? '' : 's'}
                    {' '}({sharedTimestampMassSyncConfirmation.groups.reduce((sum, group) => sum + group.records.length, 0)} records total)?
                  </p>
                  <p className="text-muted" style={{ marginBottom: 0 }}>
                    This sends one batched request per group and shows the result of each. Every record is re-verified on the server before its timestamp is updated — records with real changes are skipped rather than synced.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeSharedTimestampMassSyncConfirmation}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={confirmSharedTimestampMassSync}>
                    Start Sync
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={closeSharedTimestampMassSyncConfirmation} />
        </>
      )}

      {asyncGroupSyncConfirmation.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Async Shared Timestamp Sync</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeAsyncGroupSyncConfirmation}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p style={{ marginBottom: '12px' }}>
                    Queue all {asyncGroupSyncConfirmation.groups.length} shared-timestamp group{asyncGroupSyncConfirmation.groups.length === 1 ? '' : 's'}
                    {' '}({asyncGroupSyncConfirmation.groups.reduce((sum, group) => sum + group.records.length, 0)} records total) for background sync?
                  </p>
                  <p className="text-muted" style={{ marginBottom: 0 }}>
                    This sends every group (and its already-loaded records) to a background job, which kicks off one child job per group instead of
                    syncing them from this page. Results won't be reflected here — reload the page later to see updated statuses. Every record is
                    still re-verified server-side before its timestamp is updated; records with real changes are skipped rather than synced.
                  </p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeAsyncGroupSyncConfirmation}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-primary" onClick={confirmAsyncGroupSync}>
                    Queue Sync
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={closeAsyncGroupSyncConfirmation} />
        </>
      )}

      {sharedTimestampMassSyncState.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">Shared Timestamp Sync Progress</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={closeSharedTimestampMassSyncModal}
                    aria-label="Close"
                    disabled={sharedTimestampMassSyncState.isRunning}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>{sharedTimestampMassSyncState.completedGroups} of {sharedTimestampMassSyncState.totalGroups} groups complete</strong>
                      <span>{massSyncProgressPercent}%</span>
                    </div>
                    <div className="progress" style={{ height: '12px' }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${massSyncProgressPercent}%` }}
                        aria-valuenow={massSyncProgressPercent}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <strong>Status:</strong>
                    <div style={{ marginTop: '6px' }}>
                      {sharedTimestampMassSyncState.isRunning
                        ? `Syncing group ${sharedTimestampMassSyncState.currentLabel || '...'}...`
                        : 'Sync complete.'}
                    </div>
                  </div>

                  <div>
                    <strong>Results by group ({sharedTimestampMassSyncState.results.length})</strong>
                    <div
                      className="border rounded mt-2 p-2"
                      style={{ maxHeight: '320px', overflowY: 'auto', backgroundColor: '#fafafa' }}
                    >
                      {sharedTimestampMassSyncState.results.length === 0 ? (
                        <div className="text-muted">No results yet.</div>
                      ) : (
                        sharedTimestampMassSyncState.results.map((result) => (
                          <div key={result.timestamp} className="border-bottom pb-2 mb-2">
                            <div>
                              <strong>{formatDate(result.timestamp)}</strong>{' '}
                              <span className={result.failed > 0 || result.requestError ? 'text-danger' : 'text-success'}>
                                {result.succeeded} succeeded, {result.failed} failed (of {result.recordCount})
                              </span>
                            </div>
                            {result.requestError && (
                              <div style={{ whiteSpace: 'pre-line' }}>{result.requestError}</div>
                            )}
                            {result.failures.length > 0 && (
                              <ul style={{ marginBottom: 0 }}>
                                {result.failures.map((failure, index) => (
                                  <li key={`${result.timestamp}-${failure.hubspot_id}-${index}`}>
                                    #{failure.hubspot_id}: {failure.error}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeSharedTimestampMassSyncModal}
                    disabled={sharedTimestampMassSyncState.isRunning}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={sharedTimestampMassSyncState.isRunning ? undefined : closeSharedTimestampMassSyncModal} />
        </>
      )}

      {bulkSyncState.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">Bulk Sync Progress</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={closeBulkSyncModal}
                    aria-label="Close"
                    disabled={bulkSyncState.isRunning}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>{bulkSyncState.completed} of {bulkSyncState.total} complete</strong>
                      <span>{bulkProgressPercent}%</span>
                    </div>
                    <div className="progress" style={{ height: '12px' }}>
                      <div
                        className="progress-bar"
                        role="progressbar"
                        style={{ width: `${bulkProgressPercent}%` }}
                        aria-valuenow={bulkProgressPercent}
                        aria-valuemin="0"
                        aria-valuemax="100"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <strong>Status:</strong>
                    <div style={{ marginTop: '6px' }}>
                      {bulkSyncState.isRunning
                        ? `Syncing ${bulkSyncState.currentLabel || 'next engagement'}...`
                        : 'Bulk sync complete.'}
                    </div>
                  </div>

                  <div>
                    <strong>Failures ({bulkSyncState.failures.length})</strong>
                    <div
                      className="border rounded mt-2 p-2"
                      style={{ maxHeight: '260px', overflowY: 'auto', backgroundColor: '#fafafa' }}
                    >
                      {bulkSyncState.failures.length === 0 ? (
                        <div className="text-muted">No failures yet.</div>
                      ) : (
                        bulkSyncState.failures.map((failure) => (
                          <div key={failure.id} className="border-bottom pb-2 mb-2">
                            <div><strong>{failure.label}</strong></div>
                            <div style={{ whiteSpace: 'pre-line' }}>{failure.message}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeBulkSyncModal}
                    disabled={bulkSyncState.isRunning}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={bulkSyncState.isRunning ? undefined : closeBulkSyncModal} />
        </>
      )}

      {/* Sync Preview Modal */}
      {syncPreview.isOpen && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {syncPreview.isNewRecord ? 'Create Engagement From HubSpot' : 'Sync Engagement With HubSpot'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeSyncPreview}
                    aria-label="Close"
                    disabled={syncPreview.isSaving}
                  />
                </div>
                <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                  {syncPreview.isLoading && <div className="text-muted">Loading preview...</div>}

                  {syncPreview.error && (
                    <div className="alert alert-danger" role="alert" style={{ whiteSpace: 'pre-line' }}>
                      {syncPreview.error}
                    </div>
                  )}

                  {!syncPreview.isLoading && !syncPreview.error && syncPreview.fields && (
                    <>
                      {!syncPreview.fields.some((field) => field.changed) && (
                        <div className="text-muted mb-2">No changes — this record already matches HubSpot.</div>
                      )}
                      <table className="table table-sm table-bordered" style={{ marginBottom: 0 }}>
                        <thead>
                          <tr>
                            <th>Property</th>
                            <th>Old Value</th>
                            <th>New Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {syncPreview.fields.map(({ property, from, to, changed }) => (
                            <tr key={property}>
                              <td>{humanizeDiffKey(property)}</td>
                              <td>{formatDiffValue(from)}</td>
                              <td style={changed ? { backgroundColor: 'rgba(40, 167, 69, 0.2)', fontWeight: 600 } : undefined}>
                                {formatDiffValue(to)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeSyncPreview}
                    disabled={syncPreview.isSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmSyncPreview}
                    disabled={syncPreview.isLoading || syncPreview.isSaving || Boolean(syncPreview.error) || !(syncPreview.fields || []).some((field) => field.changed)}
                  >
                    {syncPreview.isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={syncPreview.isSaving ? undefined : closeSyncPreview} />
        </>
      )}

      {/* Error Modal */}
      {modalMessage && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">Sync Failed</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => setModalMessage(null)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body">
                  <p style={{ whiteSpace: 'pre-line' }}>{modalMessage}</p>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModalMessage(null)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={() => setModalMessage(null)} />
        </>
      )}

      {/* JSON Viewer Modal */}
      {jsonModal && (
        <>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Record JSON</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setJsonModal(null)}
                    aria-label="Close"
                  />
                </div>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  <pre style={{ fontSize: '12px', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {JSON.stringify(jsonModal, null, 2)}
                  </pre>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setJsonModal(null)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show" onClick={() => setJsonModal(null)} />
        </>
      )}

      {/* Success Toast */}
      {successToastMessage && (
        <div
          className="alert alert-success shadow"
          role="status"
          style={{
            position: 'fixed',
            right: '16px',
            bottom: '16px',
            margin: 0,
            zIndex: 2000,
            minWidth: '280px',
            maxWidth: '420px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span>{successToastMessage}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setSuccessToastMessage(null)}
          />
        </div>
      )}
    </>
  );
}

const element = document.getElementById('engagements-table');
if (element) {
  const root = createRoot(element);
  root.render(<EngagementsTable />);
}